// System
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Components
import { TableColumnHeader } from '@Components/shared/tables/columns';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';

// Contracts
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { Filterable } from '@contracts/common/traits/Filterable';
import { FailedSendCreditTasksDto } from '@contracts/api/dto/failedSendCreditTasksDto/FailedSendCreditTasksDto';

// Utils
import {
  FailedSendCreditTasksListColumns,
  FailedSendCreditTasksListFilter,
  failedSendCreditTasksListColumns,
} from '@utils/tables/failedSendCreditTasks/failedSendCreditTasksListFilter';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { parseISO } from 'date-fns';
import { convertTimestampToISOString } from '@Components/shared/labels/DateLabel/DateLabel';

type HeaderOptions = {
  filter: Filterable<FailedSendCreditTasksListFilter>;
};

type Columns = TableColumnDescriptionArray<
  FailedSendCreditTasksDto,
  HeaderOptions,
  typeof failedSendCreditTasksListColumns
>;

type Column = TableColumnDescription<
  FailedSendCreditTasksDto,
  HeaderOptions,
  FailedSendCreditTasksListColumns
>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useFailedSendCreditTasksTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'failedSendCreditTasks.id',
          title: 'ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>{row?.id && <TruncateText text={row?.id} maxLength={8} textStyles={styles.id} />}</>
          ),
        },
        {
          name: 'failedSendCreditTasks.attempts',
          title: 'Attempts',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.attempts}</Box>,
        },
        {
          name: 'failedSendCreditTasks.finishedOn',
          title: 'Finished On',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.finishedOn && (
                <DateLabel
                  date={new Date(parseISO(convertTimestampToISOString(row?.finishedOn)))}
                  format="longWithSeconds"
                />
              )}
            </Box>
          ),
        },
        {
          name: 'failedSendCreditTasks.processedOn',
          title: 'Processed On',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.processedOn && (
                <DateLabel
                  date={new Date(parseISO(convertTimestampToISOString(row?.processedOn)))}
                  format="longWithSeconds"
                />
              )}
            </Box>
          ),
        },
        {
          name: 'failedSendCreditTasks.failedReason',
          title: 'Failed Reason',
          sx: { minWidth: '300px', maxWidth: '300px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.failedReason}</Box>,
        },

        {
          name: 'failedSendCreditTasks.data.transactionId',
          title: 'Transaction ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.transactionId && (
                <TruncateText
                  text={row?.data?.transactionId}
                  maxLength={8}
                  textStyles={styles.id}
                />
              )}
            </>
          ),
        },
        {
          name: 'failedSendCreditTasks.data.roundId',
          title: 'Round ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.roundId && (
                <TruncateText text={row?.data?.roundId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'failedSendCreditTasks.data.providerId',
          title: 'Provider ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.providerId && (
                <TruncateText text={row?.data?.providerId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'failedSendCreditTasks.data.playerId',
          title: 'Player ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.playerId && (
                <TruncateText text={row?.data?.playerId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'failedSendCreditTasks.data.betId',
          title: 'Bet ID',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.betId && (
                <TruncateText text={row?.data?.betId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
